import { useAuth, useOrganization } from "@clerk/clerk-react";
import {
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
	type ReactNode,
} from "react";
import { ensurePlatformSession } from "./connections-api.js";

type Props = {
	children: ReactNode;
};

type SessionContextValue = {
	/** Platform `mairko_session` cookie has been minted. */
	ready: boolean;
	tenantId: string | null;
	tenantName: string | null;
	/** Clerk org already bound to this tenant — activate it, don’t create another. */
	clerkOrgId: string | null;
	setTenantName: (name: string) => void;
};

const PlatformSessionContext = createContext<SessionContextValue>({
	ready: false,
	tenantId: null,
	tenantName: null,
	clerkOrgId: null,
	setTenantName: () => {},
});

/** True once `/api/session/clerk` has succeeded for this Clerk user. */
export function usePlatformSessionReady(): boolean {
	return useContext(PlatformSessionContext).ready;
}

export function usePlatformSession(): SessionContextValue {
	return useContext(PlatformSessionContext);
}

/**
 * After Clerk sign-in, mint the platform session cookie so /api/connections
 * and /oauth/* (proxied) authorize as this tenant.
 *
 * A Clerk organization is optional. Personal accounts map to a tenant by
 * `users.clerkUserId`. Workspace name is `tenants.name` (Jules), not Clerk.
 *
 * Renders the desk immediately — session mint runs in the background.
 * Only a hard mint failure takes over the tree (with Retry).
 */
export function PlatformSession({ children }: Props) {
	const { isLoaded, isSignedIn, getToken } = useAuth();
	const { organization, isLoaded: orgLoaded } = useOrganization();
	const [phase, setPhase] = useState<
		"idle" | "loading" | "ready" | "error"
	>("idle");
	const [error, setError] = useState<string | null>(null);
	const [retryTick, setRetryTick] = useState(0);
	const [tenantId, setTenantId] = useState<string | null>(null);
	const [tenantName, setTenantName] = useState<string | null>(null);
	const [clerkOrgId, setClerkOrgId] = useState<string | null>(null);
	/** skipCache only after a failed mint — cold reload should use Clerk cache. */
	const forceFreshToken = useRef(false);
	const getTokenRef = useRef(getToken);
	getTokenRef.current = getToken;

	const orgId = organization?.id ?? null;
	const orgName = organization?.name ?? null;

	useEffect(() => {
		if (!isLoaded || !orgLoaded) return;
		if (!isSignedIn) {
			setPhase("idle");
			setError(null);
			setTenantId(null);
			setTenantName(null);
			setClerkOrgId(null);
			return;
		}

		const ac = new AbortController();
		setPhase("loading");
		setError(null);

		const skipCache = forceFreshToken.current;
		forceFreshToken.current = false;

		void (async () => {
			const result = await ensurePlatformSession({
				getToken: () =>
					getTokenRef.current({
						...(orgId ? { organizationId: orgId } : {}),
						skipCache,
					}),
				orgName,
				orgId,
				signal: ac.signal,
				timeoutMs: 20_000,
			});
			if (ac.signal.aborted) return;
			if (!result.ok) {
				if (result.error === "session_aborted") return;
				setError(result.error);
				setPhase("error");
				return;
			}
			setTenantId(result.tenantId);
			setTenantName(result.tenantName);
			setClerkOrgId(result.clerkOrgId);
			setPhase("ready");
		})();

		return () => {
			ac.abort();
		};
		// Intentionally omit getToken (unstable identity) and orgName (often
		// arrives a tick after orgId — reminting cancels the first request).
	}, [isLoaded, orgLoaded, isSignedIn, orgId, retryTick]);

	if (!isSignedIn) {
		return <>{children}</>;
	}

	if (phase === "error") {
		return (
			<div className="app-frame">
				<div className="auth-boot">
					<p className="auth-boot-copy">
						Could not link this account to the platform ({error}).
						{error === "clerk_not_configured"
							? " Set CLERK_SECRET_KEY on mairko-platform-v2 (wrangler secret / .dev.vars locally) and redeploy or restart."
							: error === "invalid_token"
								? " Clerk JWT did not verify. Matrix’s publishable key and mairko-platform-v2’s CLERK_SECRET_KEY must be the same Clerk instance (both test, or both live)."
								: error === "session_timeout"
								? " Timed out talking to the backend / Clerk. Locally: confirm npm run dev:backend is up on :5173. Production: confirm mairko-platform-v2 is deployed."
								: error === "session_405"
									? " Matrix is serving the SPA for /api (no Worker proxy). Redeploy frontend/matrix with the PLATFORM service binding to mairko-platform-v2."
									: error?.startsWith("session_502") ||
										  error?.startsWith("session_503")
										? " Matrix could not reach the backend — locally start npm run dev:backend; in production deploy mairko-platform-v2 and confirm the Matrix PLATFORM service binding."
										: " Check that the platform is running and CLERK_SECRET_KEY is set."}
					</p>
					<button
						type="button"
						className="btn btn-accent"
						onClick={() => {
							forceFreshToken.current = true;
							setRetryTick((n) => n + 1);
						}}
					>
						Retry
					</button>
				</div>
			</div>
		);
	}

	return (
		<PlatformSessionContext.Provider
			value={{
				ready: phase === "ready",
				tenantId,
				tenantName,
				clerkOrgId,
				setTenantName,
			}}
		>
			{children}
		</PlatformSessionContext.Provider>
	);
}
